import React, {useState, useEffect} from 'react';
import RoundRobinStandings from "../CompetitionSystems/RoundRobinStandings/RoundRobinStandings";
import {fetchMatches, fetchRoundRobinStandings, saveMatchResult} from "../../api/api";
import MatchesTable from "../CompetitionSystems/MatchesTable/MatchesTable";
import MatchResultModal from "../CompetitionSystems/MatchResultModal/MatchResultModal";
import CustomModal from "../../UI/Modal/CustomModal";
import styles from './TourCurForm.module.css';
import SingleEliminationBracket from "../CompetitionSystems/SingleEliminationBracket/SingleEliminationBracket";
import {RiRefreshLine} from "react-icons/ri";
import BrazilianBracket from "../CompetitionSystems/BrazilianBracket/BrazilianBracket";


const TourCurForm = ({tournament, isArchived}) => {
    const [standings, setStandings] = useState([]);
    const [matches, setMatches] = useState([]);
    const [editingMatchId, setEditingMatchId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [isRefreshing, setIsRefreshing] = useState(false);

    const competitionSystemMapping = {
        ROUND_ROBIN: 'każdy z każdym',
        KNOCKOUT: 'pucharowy',
        BRAZILIAN: 'brazylijski do dwóch przegranych'
    };


    const fetchMatchData = async () => {
        const matchesResult = await fetchMatches(tournament.id);
        if (matchesResult.success) {
            setMatches(matchesResult.data);
        } else {
            console.error('Could not fetch matches:', matchesResult.data);
        }
    };

    const fetchStandingsData = async () => {
        const result = await fetchRoundRobinStandings(tournament.id);
        if (result.success) {
            setStandings(result.data);
        } else {
            // Handle failure
            console.error('Could not fetch standings');
        }
    };

    const refreshData = async () => {
        setIsRefreshing(true); // Start the refresh animation
        await new Promise(resolve => setTimeout(resolve, 800));
        loadData();
    }

    const loadData = async () => {
        if (tournament) {
            if (tournament.competitionSystem === 'ROUND_ROBIN') {
                await fetchMatchData();
                await fetchStandingsData();
            } else if (tournament.competitionSystem === 'KNOCKOUT' || tournament.competitionSystem === 'BRAZILIAN') {
                await fetchMatchData();
            }
        }
        setIsRefreshing(false); // Stop the refresh animation
    };


    useEffect(() => {
        loadData();
    }, [tournament]);


    const handleEditMatch = (matchId) => {
        // Find the match that's being edited
        setEditingMatchId(matchId);
        const matchToEdit = matches.find(match => match.id === matchId);
        if (matchToEdit) {
            // Open the modal and pass the existing match data for editing
            // setEditingMatch(matchToEdit); // This would be a new piece of state you need to add
            setIsModalOpen(true);
        } else {
            console.error('Match not found!');
        }
    };

    const onSaveResult = async (matchId, scores, walkover, walkoverWinnerId, injuryResignation, injuryResignationParticipantId, clearMatchData) => {
        const matchResult = {
            matchId,
            scores,
            walkover: walkover,
            walkoverWinnerId: walkoverWinnerId,
            injuryResignation: injuryResignation,
            injuryResignationParticipantId: injuryResignationParticipantId,
            clearMatchData: clearMatchData
        };

        const response = await saveMatchResult(matchResult);
        if (response.success) {
            setModalMessage(response.data);
            setIsCustomModalOpen(true);
            setIsModalOpen(false);
            setEditingMatchId(null);
            await fetchMatchData();
            await fetchStandingsData();
        } else {
            console.error('Failed to enter result of the match:', response.data);
            setModalMessage(response.data || 'error');
            setIsCustomModalOpen(true);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingMatchId(null);

    };

    const currentMatch = matches.find(match => match.id === editingMatchId);

    const handleCloseCustomModal = () => {
        setIsCustomModalOpen(false);
    };


    const renderCompetitionSystem = () => {
        switch (tournament.competitionSystem) {
            case 'ROUND_ROBIN':
                return (
                    <>
                        {standings.length > 0 &&
                            <RoundRobinStandings standings={standings} matches={matches} isArchived={isArchived}/>
                        }
                        <MatchesTable matches={matches} onEditMatch={handleEditMatch} isArchived={isArchived}/>
                        {currentMatch && (
                            <MatchResultModal
                                isOpen={isModalOpen}
                                onRequestClose={handleModalClose}
                                onSaveResult={onSaveResult}
                                editingMatch={currentMatch}
                                matchId={editingMatchId}
                                participant1Id={currentMatch.participant1Id}
                                participant2Id={currentMatch.participant2Id}
                                participant1FirstName={currentMatch.participant1FirstName}
                                participant1LastName={currentMatch.participant1LastName}
                                participant2FirstName={currentMatch.participant2FirstName}
                                participant2LastName={currentMatch.participant2LastName}
                                participant1PartnerFirstName={currentMatch.participant1PartnerFirstName}
                                participant1PartnerLastName={currentMatch.participant1PartnerLastName}
                                participant2PartnerFirstName={currentMatch.participant2PartnerFirstName}
                                participant2PartnerLastName={currentMatch.participant2PartnerLastName}
                                isThirdSetTiebreak={tournament.isThirdSetTiebreak}
                            />
                        )}
                    </>
                );
            case 'KNOCKOUT':
                return (
                    <>
                        <SingleEliminationBracket matches={matches} onEditMatch={handleEditMatch}
                                                  isArchived={isArchived}/>
                        {currentMatch && (
                            <MatchResultModal
                                isOpen={isModalOpen}
                                onRequestClose={handleModalClose}
                                onSaveResult={onSaveResult}
                                editingMatch={currentMatch}
                                matchId={editingMatchId}
                                participant1Id={currentMatch.participant1Id}
                                participant2Id={currentMatch.participant2Id}
                                participant1FirstName={currentMatch.participant1FirstName}
                                participant1LastName={currentMatch.participant1LastName}
                                participant2FirstName={currentMatch.participant2FirstName}
                                participant2LastName={currentMatch.participant2LastName}
                                participant1PartnerFirstName={currentMatch.participant1PartnerFirstName}
                                participant1PartnerLastName={currentMatch.participant1PartnerLastName}
                                participant2PartnerFirstName={currentMatch.participant2PartnerFirstName}
                                participant2PartnerLastName={currentMatch.participant2PartnerLastName}
                            />
                        )}
                    </>
                );
            case 'BRAZILIAN':
                return (
                    <>
                        <BrazilianBracket matches={matches} onEditMatch={handleEditMatch}
                                          isArchived={isArchived}/>
                        {currentMatch && (
                            <MatchResultModal
                                isOpen={isModalOpen}
                                onRequestClose={handleModalClose}
                                onSaveResult={onSaveResult}
                                editingMatch={currentMatch}
                                matchId={editingMatchId}
                                participant1Id={currentMatch.participant1Id}
                                participant2Id={currentMatch.participant2Id}
                                participant1FirstName={currentMatch.participant1FirstName}
                                participant1LastName={currentMatch.participant1LastName}
                                participant2FirstName={currentMatch.participant2FirstName}
                                participant2LastName={currentMatch.participant2LastName}
                                participant1PartnerFirstName={currentMatch.participant1PartnerFirstName}
                                participant1PartnerLastName={currentMatch.participant1PartnerLastName}
                                participant2PartnerFirstName={currentMatch.participant2PartnerFirstName}
                                participant2PartnerLastName={currentMatch.participant2PartnerLastName}
                            />
                        )}
                    </>
                );
            default:
                return <p>Unsupported competition system</p>;
        }
    };

    return (
        <>
            <CustomModal
                isOpen={isCustomModalOpen}
                onRequestClose={handleCloseCustomModal}
                message={modalMessage}
                isPositiveMessage={modalMessage.includes("zrobione")}
                cancelLabel="Zamknij"
                confirmButtonColor="red"
                cancelButtonColor="green"
            />
            <div className={styles.container}>
                <h5 className={styles.title}>{tournament.title}
                    {!isArchived &&
                        <button
                            className={`${styles.refreshButton} ${isRefreshing ? styles.refreshButtonAnimating : ''}`}
                            onClick={refreshData}
                        >
                            <RiRefreshLine/>
                        </button>
                    }
                </h5>
                <div className={styles.competitionSystemDisplay}>
                    System: {competitionSystemMapping[tournament.competitionSystem]}
                </div>
                {!isArchived &&
                    <div className={styles.scoreRules}>
                        <a href="/score-rules" target="_blank">Zasady gry</a>
                    </div>
                }
                {renderCompetitionSystem()}
            </div>
        </>
    );
};

export default TourCurForm;
